import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngxs/store';
import { lastValueFrom } from 'rxjs';
import { LOGIN_NEW_URL } from 'src/app/services/app.constants';
import { ActivateCookieAuth, InvalidateUser, SetImpersonatePlan, TokenLogin } from 'src/app/store/auth/auth.actions';

export interface TokenLoginSpec {
  token?: string;
  company?: string;
  adminLogin?: string;
  impersonate?: string;
  authMode?: 'v1' | 'v2';
  'first-login'?: string;
}

const PARAMS_TO_STRIP: (keyof TokenLoginSpec | 'from2a')[] = ['company', 'token', 'authMode', 'first-login', 'from2a'];

const AUTH_ERROR_CODES = new Set(['invalidToken', 'invalidCredentials', 'expiredToken', 'expiredRefreshToken', 'denied']);

@Injectable({
  providedIn: 'root',
})
export class TokenLoginGuard implements CanActivate, CanActivateChild {
  constructor(
    private store: Store,
    private router: Router,
    @Inject(LOGIN_NEW_URL) private loginNewUrl,
  ) { }

  async canActivate(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const query = childRoute.queryParams as TokenLoginSpec;
    if (!query) return true;

    const token = query.token;
    const isOnboardingAuthV2 = query.authMode === 'v2' && query['first-login'] === '1';
    const shouldHandle = !!token || isOnboardingAuthV2;
    if (!shouldHandle) return true;

    const adminLogin = parseInt(query.adminLogin ?? '', 10) === 1;
    const impersonate = parseInt(query.impersonate ?? '', 10) === 1;
    const authMode = query.authMode === 'v1' || query.authMode === 'v2' ? query.authMode : undefined;

    try {
      if (isOnboardingAuthV2) {
        await lastValueFrom(this.store.dispatch(new ActivateCookieAuth(query.company, adminLogin)));
      } else {
        if (!token) return true;
        await lastValueFrom(this.store.dispatch(new TokenLogin(token, query.company ?? '', adminLogin, authMode)));
      }
      if (impersonate) {
        await lastValueFrom(this.store.dispatch(new SetImpersonatePlan()));
      }
    } catch (err: any) {
      if (err?.error && AUTH_ERROR_CODES.has(err.error)) {
        await lastValueFrom(this.store.dispatch(new InvalidateUser()));
        return this.router.parseUrl(this.loginNewUrl);
      }
    }

    const preservedParams = { ...childRoute.queryParams };
    PARAMS_TO_STRIP.forEach(key => delete preservedParams[key]);

    const urlTree = this.router.parseUrl(state.url);
    const pathSegments = urlTree.root.children?.primary?.segments?.map(it => it.path) || [];
    return this.router.createUrlTree(pathSegments.length > 0 ? pathSegments : ['/'], {
      queryParams: preservedParams,
      fragment: urlTree.fragment || childRoute.fragment,
    });
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.canActivate(childRoute, state);
  }
}
